import { observer } from 'mobx-react';
import moment from 'moment';
import React, { useContext, useMemo } from 'react';
import { DATE_FORMAT_SHORTER } from '../../../config';
import { DEBTOR } from '../../../routes';
import { Case } from '../../../server-api/model';
import { CaseSorter } from '../../../state/caseSorter';
import { searchContext } from '../../../state/search/searchState';
import { checkCazeClosed } from '../../../state/search/utils';
import { formatNumber } from '../../../utils/formatNumber';
import { PlaceholderSorter, SortHeaderCell } from './SortHeaderCell';
import './Table.css';
import { TableRowWrapper } from './TableRow';
import { useIsSmallViewport } from 'utils/hooks';

export const caseCellsHeaders: Record<string, string> = {
  caseRef: 'Case Ref',
  contract: 'Contract',
  warrant: 'Warrant/LO date',
  originalDebt: 'Amount',
  stage: 'Stage',
  name: 'Name',
  postcode: 'Postcode',
  vrm: 'VRM/Summons',
  hold: 'Hold',
  status: 'Status',
  balance: 'Balance',
};

interface TableProps {
  entries: Case[];
  selectionPool: Map<any, any>;
  expansion?: React.ReactNode;
  bookmarks?: boolean;
  onClick?: () => void;
  toOpen?: string;
  sorter: CaseSorter | PlaceholderSorter;
  labelledBy?: string;
}

export const DebtorCaseTable = observer(
  ({
    entries,
    expansion,
    bookmarks,
    onClick,
    selectionPool,
    toOpen,
    sorter,
    labelledBy,
  }: TableProps) => {
    const isSmallViewport = useIsSmallViewport();

    const jsxEntries = useMemo(() => {
      return entries.map((caze, index) => {
        const cellsToShow = {
          caseRef: caze.clientReference,
          contract: caze.contract.name,
          warrant:
            caze.warrant && caze.warrant.warrantDate
              ? moment(caze.warrant.warrantDate).format(
                DATE_FORMAT_SHORTER
              )
              : '',
          originalDebt: '£' + (formatNumber(caze.debtOnly) || '0.00'),

          stage: checkCazeClosed(caze.status)
            ? ''
            : caze.lastStage && caze.lastStage.name
              ? caze.lastStage.name
              : '',

          vrm: !isRoadTrafficCase(caze)
            ? caze.warrant?.offenceNumber
            : caze.originalVrm || '',
          hold: caze.isOnHold ? 'Yes' : 'No',
          status: caze.status
            ? (
              caze.status.charAt(0).toUpperCase() +
              caze.status.slice(1).toLowerCase()
            ).replace(/_/g, ' ')
            : '',
          balance: '£' + formatNumber(caze.debtBalance),
        };

        const link = DEBTOR + '/' + caze.debtor.id + '/cases/' + caze.id;

        return (
          <TableRowWrapper
            cellsToShow={cellsToShow}
            cellsHeaders={caseCellsHeaders}
            bookmarks={bookmarks}
            key={'row-' + caze.id}
            item={caze}
            onClickLink={onClick}
            selectionPool={selectionPool}
            link={link}
            compactViewMode={isSmallViewport}
            colSpan={11}
          />
        );
      })
    }, [entries, isSmallViewport]);

    return isSmallViewport ? <div className='StandardTable'>{jsxEntries}</div> :
      (
        <table
          className='StandardTable'
          aria-labelledby={labelledBy}
        >
          <thead>
            <tr className='header'>
              {expansion && (
                <th className='cell-chevron'>
                  <span className='hidden-header'>Expand</span>
                </th>
              )}
              {bookmarks && (
                <th className='cell-bookmark'>
                  <span className='hidden-header'>Bookmark</span>
                </th>
              )}

              <SortHeaderCell
                mode='td'
                sorter={sorter}
                type='clientReference'
                className='cell-caseref'
                text={caseCellsHeaders.caseRef}
              />
              <SortHeaderCell
                mode='td'
                sorter={sorter}
                type='contract'
                className='cell-contract'
                text={caseCellsHeaders.contract}
              />
              <SortHeaderCell
                mode='td'
                sorter={sorter}
                type='warrantDate'
                className='cell-warrant'
                text={caseCellsHeaders.warrant}
              />
              <SortHeaderCell
                mode='td'
                sorter={sorter}
                type='debtOnly'
                className='cell-originaldebt'
                text={caseCellsHeaders.originalDebt}
              />
              <SortHeaderCell
                mode='td'
                sorter={sorter}
                type='stage'
                text={caseCellsHeaders.stage}
                className='cell-stage'
              />
              <SortHeaderCell
                mode='td'
                sorter={sorter}
                type='originalVrm'
                text={caseCellsHeaders.vrm}
                className='cell-vrm'
              />
              <SortHeaderCell
                mode='td'
                sorter={sorter}
                className='cell-hold'
                type='isOnHold'
                text={caseCellsHeaders.hold}
              />
              <SortHeaderCell
                mode='td'
                sorter={sorter}
                className='cell-status'
                type='status'
                text={caseCellsHeaders.status}
              />
              <SortHeaderCell
                mode='td'
                sorter={sorter}
                className='cell-balance'
                type='debtBalance'
                text={caseCellsHeaders.balance}
              />

              <th className='cell-checkbox'>
                <span className='hidden-header'>Select</span>
              </th>
            </tr>
          </thead>

          <tbody className='table-body'>
            {jsxEntries}
          </tbody>
        </table>
      );
  }
);

export const CaseCaseTable = observer(
  ({
    entries,
    expansion,
    bookmarks,
    onClick,
    selectionPool,
    sorter,
  }: TableProps) => {
    useContext(searchContext);

    const isSmallViewport = useIsSmallViewport();

    const jsxEntries = useMemo(() => {
      return entries.map((caze, index) => {
        const cellsToShow = {
          caseRef: caze.clientReference,
          contract: caze.contract.name,
          warrant:
            caze.warrant && caze.warrant.warrantDate
              ? moment(caze.warrant.warrantDate).format(
                DATE_FORMAT_SHORTER
              )
              : '',
          originalDebt: '£' + (formatNumber(caze.debtOnly) || '0.00'),
          name: caze.debtorName,
          postcode: caze.postcode,
          vrm:
            caze.contract.workType?.workTypeGroup?.code !== 'ROAD_TRAFFIC'
              ? caze.warrant?.offenceNumber
              : caze.originalVrm || '',
          hold: caze.isOnHold ? 'Yes' : 'No',
          status: caze.status
            ? (
              caze.status.charAt(0).toUpperCase() +
              caze.status.slice(1).toLowerCase()
            ).replace(/_/g, ' ')
            : '',
          balance: '£' + formatNumber(caze.debtBalance),
        };

        const link = DEBTOR + '/' + caze.debtor.id + '/cases/' + caze.id;

        return (
          <TableRowWrapper
            cellsToShow={cellsToShow}
            cellsHeaders={caseCellsHeaders}
            bookmarks={bookmarks}
            key={'row' + caze.id}
            item={caze}
            onClickLink={onClick}
            selectionPool={selectionPool}
            link={link}
            compactViewMode={isSmallViewport}
            colSpan={12}
          />
        );
      })
    }, [entries, isSmallViewport]);

    return isSmallViewport ? <div className='StandardTable'>{jsxEntries}</div> :
      (
        <table
          className='StandardTable TableWithPostcodes'
          aria-label='Cases'
        >
          <thead>
            <tr className='header'>
              {expansion && (
                <th className='cell-chevron'>
                  <span className='hidden-header'>Expand</span>
                </th>
              )}
              {bookmarks && (
                <th className='cell-bookmark'>
                  <span className='hidden-header'>Bookmark</span>
                </th>
              )}

              <SortHeaderCell
                mode='td'
                sorter={sorter}
                type='clientReference'
                className='cell-caseref'
                text={caseCellsHeaders.caseRef}
              />
              <SortHeaderCell
                mode='td'
                sorter={sorter}
                type='contract'
                className='cell-contract'
                text={caseCellsHeaders.contract}
              />
              <SortHeaderCell
                mode='td'
                sorter={sorter}
                type='warrantDate'
                className='cell-warrant'
                text={caseCellsHeaders.warrant}
              />
              <SortHeaderCell
                mode='td'
                sorter={sorter}
                type='debtOnly'
                className='cell-originaldebt'
                text={caseCellsHeaders.originalDebt}
              />
              <SortHeaderCell
                mode='td'
                sorter={sorter}
                type='surname'
                text={caseCellsHeaders.name}
                className='cell-name'
              />
              <SortHeaderCell
                mode='td'
                sorter={sorter}
                type='postcode'
                text={caseCellsHeaders.postcode}
                className='cell-postcode'
              />
              <SortHeaderCell
                mode='td'
                sorter={sorter}
                type='originalVrm'
                text={caseCellsHeaders.vrm}
                className='cell-vrm'
              />
              <SortHeaderCell
                mode='td'
                sorter={sorter}
                className='cell-hold'
                type='isOnHold'
                text={caseCellsHeaders.hold}
              />
              <SortHeaderCell
                mode='td'
                sorter={sorter}
                className='cell-status'
                type='status'
                text={caseCellsHeaders.status}
              />
              <SortHeaderCell
                mode='td'
                sorter={sorter}
                className='cell-balance'
                type='debtBalance'
                text={caseCellsHeaders.balance}
              />

              <th className='cell-checkbox'>
                <span className='hidden-header'>Select</span>
              </th>
            </tr>
          </thead>

          <tbody className='table-body'>
            {jsxEntries}
          </tbody>
        </table>
      );
  }
);

export const isRoadTrafficCase = (caze: Case) => {
  return caze.contract.workType?.workTypeGroup?.code === 'ROAD_TRAFFIC';
};
